import type { Vec2 } from '../render/homography';

/** Rasterizes a polygon (normalized 0..1 points) into a 0/255 mask. */
export function polygonMask(points: Vec2[], width: number, height: number): Uint8Array {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  points.forEach(([x, y], i) => (i ? ctx.lineTo(x * width, y * height) : ctx.moveTo(x * width, y * height)));
  ctx.closePath();
  ctx.fill();
  return canvasToMask(c);
}

/** Reads the red channel of a canvas as a 0/255 mask. */
export function canvasToMask(c: HTMLCanvasElement): Uint8Array {
  const data = c.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, c.width, c.height).data;
  const out = new Uint8Array(c.width * c.height);
  for (let i = 0; i < out.length; i++) out[i] = data[i * 4] > 127 ? 255 : 0;
  return out;
}

/** Draws a 0/255 mask into a canvas (white = floor, transparent elsewhere). */
export function maskToCanvas(mask: Uint8Array, width: number, height: number, c = document.createElement('canvas')): HTMLCanvasElement {
  c.width = width;
  c.height = height;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  const img = ctx.createImageData(width, height);
  for (let i = 0; i < mask.length; i++) {
    const v = mask[i];
    img.data[i * 4] = 255;
    img.data[i * 4 + 1] = 255;
    img.data[i * 4 + 2] = 255;
    img.data[i * 4 + 3] = v;
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

/** Decodes a mask image (PNG URL or blob) and scales it to the given size. */
export async function loadMaskImage(src: Blob | string, width: number, height: number): Promise<Uint8Array> {
  const blob = typeof src === 'string' ? await (await fetch(src)).blob() : src;
  const bmp = await createImageBitmap(blob);
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bmp, 0, 0, width, height);
  bmp.close();
  return canvasToMask(c);
}

/** Resamples a 0/255 mask to a new size (smooth scaling, then threshold). */
export function scaleMask(mask: Uint8Array, w: number, h: number, toW: number, toH: number): Uint8Array {
  if (w === toW && h === toH) return mask;
  const src = maskToCanvas(mask, w, h);
  const c = document.createElement('canvas');
  c.width = toW;
  c.height = toH;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, toW, toH);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, toW, toH);
  return canvasToMask(c);
}

/** Fraction of pixels that are floor. */
export function coverage(mask: Uint8Array): number {
  let n = 0;
  for (let i = 0; i < mask.length; i++) if (mask[i] > 127) n++;
  return n / mask.length;
}
